import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, useIsMutating, useQueryClient } from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';
import {
  Check,
  CloudOff,
  Flag,
  Inbox,
  Mail,
  Paperclip,
  PenLine,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { ToastProvider, TooltipProvider, cn, useToast } from '@waymail/ui';
import { AvisoDeEnvioPresa } from './AvisoDeEnvioPresa';
import { CachePersistido } from './CachePersistido';
import { MenuDoUsuario } from './MenuDoUsuario';
import { ListaDeRascunhos } from './ListaDeRascunhos';
import { MessageList } from './MessageList';
import { ReadingPane } from './ReadingPane';
import { Resizer } from './Resizer';
import { Rail } from './Rail';
import { Sidebar } from './Sidebar';
import { aoExpirar, definirToken, renovar, sair, usuarioAtual } from '../lib/api';
import { descreverAdiamento, horarioDeAdiamento } from '../lib/adiar';
import {
  chaves,
  conversasDaPagina,
  registrarFila,
  useAdiar,
  useBuscasSalvas,
  useContas,
  useConversas,
  usePastas,
  usePreferencias,
  useSincronizar,
} from '../lib/consultas';
import {
  criarPersistidor,
  definirDonoDoCache,
  donoDoCache,
  VALIDADE_DO_CACHE,
} from '../lib/offline';
import { acaoDaUrl, semAcao } from '../lib/mailto';
import { filtrosEfetivos, operadoresDosFiltros } from '../lib/filtrosDaLista';
import { lerRascunho } from '../lib/rascunho';
import { useOnline } from '../lib/rede';
import { useShortcuts } from '../lib/shortcuts';
import { desfazerUltimo, useTriagem } from '../lib/triagem';
import { useTempoReal } from '../lib/tempoReal';
import { useEhAdmin } from '../lib/admin';
import { useMediaQuery } from '../lib/useMediaQuery';
import { applyPreference } from '../theme';
import { LIST_MAX, LIST_MIN, useUi } from '../store/ui';

// Entrar e AdicionarConta so servem a quem ainda nao entrou ou nao tem caixa:
// quem volta ao e-mail nao paga os ~27 KB deles (com a tela de marca) no
// carregamento inicial, que e o que o LCP mede.
// Bandeja, badge e links do app desktop: so dentro do Tauri.
const PonteDesktop = lazy(() =>
  import('./PonteDesktop').then((m) => ({ default: m.PonteDesktop })),
);
const emTauri = () => typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;

const Entrar = lazy(() => import('./Entrar').then((m) => ({ default: m.Entrar })));
const AdicionarConta = lazy(() =>
  import('./AdicionarConta').then((m) => ({ default: m.AdicionarConta })),
);

const CommandPalette = lazy(() =>
  import('./CommandPalette').then((m) => ({ default: m.CommandPalette })),
);
const ShortcutsHelp = lazy(() =>
  import('./ShortcutsHelp').then((m) => ({ default: m.ShortcutsHelp })),
);
// Configuracoes tambem sao modulo (ADR 0047), e o que menos se visita.
const Configuracoes = lazy(() =>
  import('./Configuracoes').then((m) => ({ default: m.Configuracoes })),
);
// Modulo inteiro sob demanda: quem so le e-mail nao paga a agenda.
const Contatos = lazy(() => import('./Contatos').then((m) => ({ default: m.Contatos })));
const CentralDeAnexos = lazy(() =>
  import('./CentralDeAnexos').then((m) => ({ default: m.CentralDeAnexos })),
);
// O TipTap e o maior pedaco do composer: so desce quando alguem escreve.
const Tarefas = lazy(() => import('./Tarefas').then((m) => ({ default: m.Tarefas })));
// So o admin abre: ninguem mais baixa o painel (ADR 0067).
const SaudeDoServico = lazy(() =>
  import('./SaudeDoServico').then((m) => ({ default: m.SaudeDoServico })),
);
const DialogoDeRegra = lazy(() =>
  import('./DialogoDeRegra').then((m) => ({ default: m.DialogoDeRegra })),
);
const DialogoDeTarefa = lazy(() =>
  import('./DialogoDeTarefa').then((m) => ({ default: m.DialogoDeTarefa })),
);
const Composer = lazy(() => import('./Composer').then((m) => ({ default: m.Composer })));

function criarQueryClient() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Secao 2.4: o cache pinta na hora e revalida atras. 30 s cobre o
        // tempo de uma triagem sem deixar a lista mentir.
        staleTime: 30_000,
        retry: 1,
        // Precisa acompanhar a validade do cache guardado: o que sai da memoria
        // antes disso some do disco junto, e a tela abriria vazia sem rede.
        gcTime: VALIDADE_DO_CACHE,
      },
    },
  });

  registrarFila(queryClient);
  return queryClient;
}

export function AppShell() {
  // Um cliente por montagem, nao por modulo: cliente de modulo compartilha
  // cache entre montagens, e cache de outro usuario e vazamento de dado.
  const [queryClient] = useState(criarQueryClient);
  const [persistidor] = useState(criarPersistidor);

  return (
    <CachePersistido client={queryClient} persister={persistidor}>
      <TooltipProvider>
        <ToastProvider>
          <Portao persistidor={persistidor} />
        </ToastProvider>
      </TooltipProvider>
    </CachePersistido>
  );
}

/** Decide entre entrar, adicionar caixa ou usar o produto. */
function Portao({ persistidor }: { persistidor: Persister }) {
  const [autenticado, setAutenticado] = useState(false);
  const [verificando, setVerificando] = useState(true);
  const [adicionando, setAdicionando] = useState(false);
  const queryClient = useQueryClient();
  const online = useOnline();

  /**
   * O cache guardado e a fila sao de quem estava logado. Se quem entra e outra
   * pessoa, nada do que ficou no disco pode aparecer na tela dela.
   */
  const reconhecerDono = useCallback(
    (userId: string) => {
      if (donoDoCache() !== userId) {
        queryClient.clear();
        void persistidor.removeClient();
      }
      definirDonoDoCache(userId);
    },
    [queryClient, persistidor],
  );

  // O cookie de refresh sobrevive ao recarregamento; o access token nao.
  useEffect(() => {
    let vivo = true;

    aoExpirar(() => setAutenticado(false));

    void renovar()
      .then((ok) => {
        if (!vivo) return;
        const dono = usuarioAtual();
        if (ok && dono !== null) reconhecerDono(dono.id);
        setAutenticado(ok);
      })
      .catch(() => {
        // Sem rede nao da para provar a sessao. Quem ja usou o produto nesta
        // maquina continua vendo o que tem guardado; a prova vem quando a
        // conexao voltar, e a sessao cai ali se tiver mesmo expirado.
        if (vivo) setAutenticado(donoDoCache() !== null);
      })
      .finally(() => {
        if (vivo) setVerificando(false);
      });

    return () => {
      vivo = false;
    };
  }, [reconhecerDono]);

  const contas = useContas();

  if (verificando) return <Carregando />;
  if (!autenticado)
    return (
      <Suspense fallback={<Carregando />}>
        <Entrar
          aoEntrar={(sessao) => {
            reconhecerDono(sessao.user.id);
            // A consulta das caixas rodou sem sessao e parou no erro 401; sem
            // refazer, a tela cairia em "Sem conexao" mesmo com o login feito.
            void queryClient.invalidateQueries({ queryKey: chaves.contas });
            setAutenticado(true);
          }}
        />
      </Suspense>
    );
  // Offline a consulta fica pausada, nao carregando: sem isto a tela travaria
  // no spinner ate a rede voltar.
  if (contas.isLoading) return <Carregando />;
  if (contas.data === undefined) return <SemConexao />;

  if (adicionando || contas.data.length === 0) {
    return (
      <Suspense fallback={<Carregando />}>
        <AdicionarConta aoConcluir={() => setAdicionando(false)} />
      </Suspense>
    );
  }

  return (
    <>
      {emTauri() && (
        <Suspense fallback={null}>
          <PonteDesktop />
        </Suspense>
      )}
      <Shell
        onAdicionarConta={() => setAdicionando(true)}
        onSair={async () => {
          setAutenticado(false);
          definirDonoDoCache(null);
          queryClient.clear();
          await persistidor.removeClient();
          // Por ultimo: e a unica parte que depende da rede.
          if (online) await sair();
          else definirToken(null);
        }}
      />
    </>
  );
}

function SemConexao() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-sunken px-6 text-center">
      <CloudOff size={28} strokeWidth={1.5} aria-hidden="true" className="text-tertiary" />
      <p className="text-[15px] font-semibold text-primary">Sem conexão</p>
      <p className="max-w-sm text-[13px] text-tertiary">
        Ainda não há nada guardado nesta máquina para mostrar. Assim que a conexão voltar, sua caixa
        aparece aqui.
      </p>
    </div>
  );
}

function Carregando() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-sunken">
      <RefreshCw
        size={24}
        aria-label="Carregando"
        className="animate-spin text-tertiary motion-reduce:animate-none"
      />
    </div>
  );
}

interface ShellProps {
  onAdicionarConta: () => void;
  onSair: () => void;
}

function Shell({ onAdicionarConta, onSair }: ShellProps) {
  const toast = useToast();
  const online = useOnline();
  // Mensagem nova na hora, pelo SSE (ADR 0053); sem ele, o polling de sempre.
  useTempoReal(() => toast({ title: 'Hora de acompanhar uma conversa sem resposta.' }));
  // Ações feitas offline (e as que ainda estão saindo) esperando a rede.
  const naFila = useIsMutating({ mutationKey: ['fila'] });

  const theme = useUi((s) => s.theme);
  const density = useUi((s) => s.density);
  const accountId = useUi((s) => s.accountId);
  const folderId = useUi((s) => s.folderId);
  const filter = useUi((s) => s.filter);
  const filtros = useUi((s) => s.filtros);
  const query = useUi((s) => s.query);
  const mensagemId = useUi((s) => s.mensagemId);
  const listWidth = useUi((s) => s.listWidth);
  const setListWidth = useUi((s) => s.setListWidth);
  const selectMensagem = useUi((s) => s.selectMensagem);
  const selectFolder = useUi((s) => s.selectFolder);
  const selectAccount = useUi((s) => s.selectAccount);
  const toggleChecked = useUi((s) => s.toggleChecked);
  const setPaletteOpen = useUi((s) => s.setPaletteOpen);
  const setAtalhosAtivos = useUi((s) => s.setAtalhosAtivos);
  const preferencias = usePreferencias();
  const setHelpOpen = useUi((s) => s.setHelpOpen);
  const paletteOpen = useUi((s) => s.paletteOpen);
  const helpOpen = useUi((s) => s.helpOpen);
  const composerOpen = useUi((s) => s.composerOpen);
  const pedidoDeTarefa = useUi((s) => s.pedidoDeTarefa);
  const pedidoDeRegra = useUi((s) => s.pedidoDeRegra);
  const setComposerOpen = useUi((s) => s.setComposerOpen);
  const pedirMenuDaConversa = useUi((s) => s.pedirMenuDaConversa);
  const abrirComposerCom = useUi((s) => s.abrirComposerCom);

  // Atalho "Novo e-mail" e link `mailto:` do app instalado (ADR 0041). Uma vez
  // so, e a URL e limpa em seguida: recarregar nao pode abrir outro composer.
  useEffect(() => {
    const acao = acaoDaUrl(window.location.search);
    if (acao === null) return;
    if (acao.tipo === 'mailto') abrirComposerCom(acao.rascunho);
    else setComposerOpen(true);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${semAcao(window.location.search)}${window.location.hash}`,
    );
  }, [abrirComposerCom, setComposerOpen]);
  const atalhosAtivos = preferencias.data?.atalhos.ativos ?? true;
  useEffect(() => {
    setAtalhosAtivos(atalhosAtivos);
  }, [atalhosAtivos, setAtalhosAtivos]);
  const modulo = useUi((s) => s.modulo);
  // Fora do e-mail, os atalhos de triagem nao agem: `e` na agenda arquivaria
  // a mensagem que ficou selecionada la atras, sem ninguem ver.
  const emMail = modulo === 'mail';

  const telaEstreita = useMediaQuery('(max-width: 767px)');
  const leituraSobreposta = useMediaQuery('(max-width: 1023px)');

  useEffect(() => {
    applyPreference(theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.dataset['density'] = density;
  }, [density]);

  const triagem = useTriagem();
  const adiar = useAdiar();

  const contas = useContas();
  const contaAtiva = useMemo(
    () => contas.data?.find((c) => c.id === accountId) ?? contas.data?.[0],
    [contas.data, accountId],
  );

  // A caixa lembrada de antes so vale se ainda existir (outro usuario, caixa
  // removida); `contaAtiva` ja cai na primeira, e o estado acompanha.
  useEffect(() => {
    if (contaAtiva !== undefined && accountId !== contaAtiva.id) selectAccount(contaAtiva.id);
  }, [accountId, contaAtiva, selectAccount]);

  const pastas = usePastas(contaAtiva?.id);

  // Lista de rascunhos do servidor (ADR 0052): na pasta Rascunhos da caixa, ou
  // sozinha quando a caixa nao tem uma.
  const verRascunhos = useUi((s) => s.verRascunhos);
  const naPastaDeRascunhos = pastas.data?.find((p) => p.id === folderId)?.role === 'drafts';
  const pastaInteligente = useUi((s) => s.pastaInteligente);
  const emRascunhos = pastaInteligente === null && (verRascunhos || naPastaDeRascunhos);

  // Recarregou com texto que o servidor ainda nao viu: reabre o composer, que o
  // restaura do buffer local e termina de salvar (ADR 0052).
  useEffect(() => {
    if (lerRascunho()?.meta?.pendente === true) setComposerOpen(true);
  }, [setComposerOpen]);

  // Sem pasta escolhida, ou com uma lembrada que nao e desta caixa (foi apagada,
  // ou veio de outra conta), cai na Entrada: pasta que nao existe deixava a lista
  // vazia, sem erro nenhum.
  useEffect(() => {
    if (pastas.data === undefined) return;
    if (folderId !== null && pastas.data.some((p) => p.id === folderId)) return;
    const entrada = pastas.data.find((p) => p.role === 'inbox') ?? pastas.data[0];
    if (entrada !== undefined) selectFolder(entrada.id);
  }, [folderId, pastas.data, selectFolder]);

  // Pasta inteligente que nao existe mais (excluida aqui, em outra aba ou lembrada
  // de antes do F5) volta a lista para a pasta de antes: sem isto ela ficava presa
  // nos resultados de uma consulta apagada, sem pasta marcada.
  const buscasSalvas = useBuscasSalvas();
  const fecharPastaInteligente = useUi((s) => s.fecharPastaInteligente);
  useEffect(() => {
    if (buscasSalvas.data === undefined || pastaInteligente === null) return;
    if (pastaInteligente.chave.startsWith('preset:')) return;
    if (!buscasSalvas.data.some((b) => b.id === pastaInteligente.chave)) fecharPastaInteligente();
  }, [buscasSalvas.data, pastaInteligente, fecharPastaInteligente]);

  // Pasta inteligente: sem pasta, a consulta dela somada a busca da lista (as
  // duas valem juntas); `accountId` nulo reune todas as caixas.
  const conversas = useConversas({
    accountId:
      pastaInteligente === null ? contaAtiva?.id : (pastaInteligente.accountId ?? undefined),
    folderId: pastaInteligente === null ? (folderId ?? undefined) : undefined,
    filter,
    q: [pastaInteligente?.query, query, operadoresDosFiltros(filtrosEfetivos(filtros, filter))]
      .filter((t) => t !== undefined && t.trim() !== '')
      .join(' ')
      .trim(),
  });

  const itens = useMemo(() => conversasDaPagina(conversas.data?.pages), [conversas.data?.pages]);
  // Na pasta Rascunhos o IMAP so ganha uma segunda lista quando tem o que mostrar:
  // vazia, ela era um "Nada por aqui" espremendo os rascunhos do servidor (B8).
  const comListaDaPasta = naPastaDeRascunhos && (conversas.isLoading || itens.length > 0);

  const scrollParaId = useRef<((id: string) => void) | null>(null);
  const registrarScroll = useCallback((fn: (id: string) => void) => {
    scrollParaId.current = fn;
  }, []);

  const mover = useCallback(
    (passo: 1 | -1) => {
      if (itens.length === 0) return;
      const atual = itens.findIndex((t) => t.messageId === mensagemId);
      const proximo = Math.min(itens.length - 1, Math.max(0, atual + passo));
      const alvo = itens[atual === -1 ? 0 : proximo];
      if (alvo === undefined) return;
      selectMensagem(alvo.messageId);
      scrollParaId.current?.(alvo.messageId);
    },
    [itens, mensagemId, selectMensagem],
  );

  const irParaPasta = useCallback(
    (papel: string) => {
      const alvo = pastas.data?.find((p) => p.role === papel);
      if (emMail && alvo !== undefined) selectFolder(alvo.id);
    },
    [pastas.data, emMail, selectFolder],
  );

  const sincronizar = useSincronizar();

  // Paleta e lazy (~390 ms na 1a abertura): baixa em ocioso, depois do LCP.
  useEffect(() => {
    const id = setTimeout(() => void import('./CommandPalette'), 3000);
    return () => clearTimeout(id);
  }, []);

  useShortcuts({
    // F9, como no Outlook: sincroniza a caixa aberta. O Chrome nao usa a tecla.
    sync: () => {
      if (accountId === null || sincronizar.isPending) return;
      // Sem aviso a tecla parecia nao fazer nada: a lista so muda se chegou mensagem.
      toast({ title: 'Sincronizando…', duration: 2000 });
      sincronizar.mutate(accountId, {
        onSuccess: () => toast({ title: 'Caixa atualizada', duration: 2500 }),
        onError: () =>
          toast({
            title: 'Não foi possível sincronizar',
            description: 'Verifique a conexão ou o estado da conta.',
            tone: 'error',
          }),
      });
    },
    goInbox: () => irParaPasta('inbox'),
    goSent: () => irParaPasta('sent'),
    goDrafts: () => irParaPasta('drafts'),
    next: () => {
      if (emMail) mover(1);
    },
    prev: () => {
      if (emMail) mover(-1);
    },
    back: () => selectMensagem(null),
    palette: () => setPaletteOpen(true),
    help: () => setHelpOpen(true),
    search: () => document.querySelector<HTMLInputElement>('input[type="search"]')?.focus(),
    check: () => {
      if (emMail && mensagemId !== null) toggleChecked(mensagemId);
    },
    compose: () => setComposerOpen(true),
    snooze: () => {
      if (!emMail || mensagemId === null) return;
      // O atalho usa o padrao mais util: volta amanha de manha. As demais
      // opcoes ficam no menu do painel de leitura.
      const alvo = horarioDeAdiamento('amanha');
      mover(1);
      adiar.mutate(
        { messageId: mensagemId, until: alvo.toISOString() },
        {
          onSuccess: () => toast({ title: `Volta em ${descreverAdiamento(alvo)}.` }),
          onError: (e) =>
            toast({
              title: e instanceof Error ? e.message : 'Não foi possível adiar.',
              tone: 'error',
            }),
        },
      );
    },
    flag: () => {
      const atual = itens.find((t) => t.messageId === mensagemId);
      if (!emMail || atual === undefined) return;
      void triagem.executar([atual.messageId], atual.flagged ? 'unflag' : 'flag');
    },
    move: () => {
      if (emMail && mensagemId !== null) pedirMenuDaConversa();
    },
    undo: () => {
      desfazerUltimo();
    },
    archive: () => {
      if (!emMail || mensagemId === null) return;
      // Sai da lista agora; o comando IMAP vai na fila do outbox (Secao 10.4).
      // Passa por `triar`: toast com Desfazer (ADR 0054).
      mover(1);
      void triagem.executar([mensagemId], 'archive', { titulo: 'Arquivada.' });
    },
    delete: () => {
      if (!emMail || mensagemId === null) return;
      mover(1);
      void triagem.executar([mensagemId], 'delete');
    },
    spam: () => {
      if (!emMail || mensagemId === null) return;
      mover(1);
      void triagem.executar([mensagemId], 'spam');
    },
  });

  const onCompose = useCallback(() => setComposerOpen(true), [setComposerOpen]);

  // Sair apaga o que esta guardado nesta maquina, inclusive a fila. Se tem
  // coisa esperando rede, avisa antes em vez de descartar em silencio.
  const sairComCuidado = useCallback(() => {
    if (naFila === 0) {
      onSair();
      return;
    }
    toast({
      title:
        naFila === 1
          ? 'Uma alteração ainda não saiu desta máquina.'
          : `${naFila} alterações ainda não saíram desta máquina.`,
      description: 'Sair agora descarta o que está na fila.',
      tone: 'error',
      action: { label: 'Sair mesmo assim', onAction: onSair },
    });
  }, [naFila, onSair, toast]);

  const noutroModuloEstreito = telaEstreita && modulo !== 'mail' && modulo !== 'settings';
  const listaVisivel = !telaEstreita || mensagemId === null;
  const leituraVisivel = !telaEstreita || mensagemId !== null;

  return (
    <div
      tabIndex={-1}
      data-foco-de-retorno=""
      className="flex h-dvh overflow-hidden bg-sunken text-primary outline-none"
    >
      <AvisoDeEnvioPresa />
      <Rail
        sync={
          !online
            ? 'offline'
            : contaAtiva?.syncState === 'syncing' || naFila > 0
              ? 'syncing'
              : 'idle'
        }
        pendentes={naFila}
        onSair={sairComCuidado}
      />

      <div
        className={cn(
          'relative flex min-w-0 flex-1 gap-2 p-2',
          // O avatar e absoluto no canto: abaixo de 1024 as telas ocupam a largura
          // toda, entao a faixa dele fica reservada em vez de cobrir a busca.
          leituraSobreposta && 'pt-14',
          // Espaco da barra de modulos, que e fixa no rodape.
          noutroModuloEstreito && 'pb-20',
        )}
      >
        {modulo === 'contacts' ? (
          <Suspense fallback={null}>
            <Contatos />
          </Suspense>
        ) : modulo === 'attachments' ? (
          <Suspense fallback={null}>
            <CentralDeAnexos />
          </Suspense>
        ) : modulo === 'tasks' ? (
          <Suspense fallback={null}>
            <Tarefas />
          </Suspense>
        ) : modulo === 'admin' ? (
          <Suspense fallback={null}>
            <SaudeDoServico />
          </Suspense>
        ) : modulo === 'settings' ? (
          <Suspense fallback={null}>
            <Configuracoes />
          </Suspense>
        ) : (
          <>
            {!telaEstreita && (
              <div className="hidden shrink-0 overflow-hidden rounded-2xl lg:flex">
                <Sidebar
                  onCompose={onCompose}
                  accountId={contaAtiva?.id ?? null}
                  pastas={pastas.data ?? []}
                  carregando={pastas.isLoading}
                  onAdicionarConta={onAdicionarConta}
                />
              </div>
            )}

            {listaVisivel && (
              <>
                <div
                  className={cn(
                    'flex min-w-0 overflow-hidden rounded-2xl',
                    telaEstreita || leituraSobreposta ? 'flex-1' : 'shrink-0',
                  )}
                  style={telaEstreita || leituraSobreposta ? undefined : { width: listWidth }}
                >
                  {emRascunhos ? (
                    <div className="flex min-w-0 flex-1 flex-col">
                      <ListaDeRascunhos
                        accountId={contaAtiva?.id}
                        compartilhada={comListaDaPasta}
                      />
                      {comListaDaPasta && (
                        <div className="flex min-h-0 min-w-0 flex-1">
                          <MessageList
                            conversas={itens}
                            carregando={conversas.isLoading}
                            temMais={conversas.hasNextPage}
                            carregarMais={() => void conversas.fetchNextPage()}
                            onListRef={registrarScroll}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <MessageList
                      conversas={itens}
                      carregando={conversas.isLoading}
                      temMais={conversas.hasNextPage}
                      carregarMais={() => void conversas.fetchNextPage()}
                      onListRef={registrarScroll}
                      falhou={conversas.isError}
                      tentarDeNovo={() => void conversas.refetch()}
                      primeiroSync={
                        contaAtiva?.lastSyncAt !== null
                          ? null
                          : contaAtiva.syncState === 'error' ||
                              contaAtiva.syncStatus === 'sem_conexao' ||
                              contaAtiva.syncStatus === 'credencial'
                            ? 'erro'
                            : 'andamento'
                      }
                    />
                  )}
                </div>
                {!telaEstreita && !leituraSobreposta && (
                  <Resizer
                    value={listWidth}
                    min={LIST_MIN}
                    max={LIST_MAX}
                    onChange={setListWidth}
                    label="Largura da lista de mensagens"
                  />
                )}
              </>
            )}

            {leituraVisivel &&
              (leituraSobreposta && !telaEstreita ? (
                mensagemId !== null && (
                  <div className="fixed inset-y-0 right-0 z-40 flex w-[min(680px,100vw)] border-l border-subtle bg-app shadow-wc-xl">
                    <ReadingPane messageId={mensagemId} onBack={() => selectMensagem(null)} />
                  </div>
                )
              ) : (
                <div className="flex min-w-0 flex-1 overflow-hidden rounded-2xl">
                  <ReadingPane
                    messageId={mensagemId}
                    {...(telaEstreita ? { onBack: () => selectMensagem(null) } : {})}
                  />
                </div>
              ))}
          </>
        )}

        <MenuDoUsuario onSair={sairComCuidado} onAdicionarConta={onAdicionarConta} />
      </div>

      {telaEstreita && emMail && mensagemId === null && <BarraInferior onCompose={onCompose} />}
      {noutroModuloEstreito && <BarraDeModulos />}

      <Suspense fallback={null}>
        {paletteOpen && <CommandPalette onCompose={onCompose} />}
        {helpOpen && <ShortcutsHelp />}
        {composerOpen && <Composer />}
        {pedidoDeTarefa !== null && <DialogoDeTarefa />}
        {pedidoDeRegra !== null && <DialogoDeRegra />}
      </Suspense>
    </div>
  );
}

/**
 * Abaixo de 768px o rail some: nas telas de Contatos, Anexos e Tarefas esta
 * barra e o caminho de volta ao e-mail e entre modulos. Sem roteador (ADR 0007),
 * so troca `modulo` no store.
 */
export function BarraDeModulos() {
  const modulo = useUi((s) => s.modulo);
  const setModulo = useUi((s) => s.setModulo);
  const admin = useEhAdmin();
  const destinos = [
    { id: 'mail', label: 'E-mail', icon: Mail },
    { id: 'contacts', label: 'Contatos', icon: Users },
    { id: 'attachments', label: 'Anexos', icon: Paperclip },
    { id: 'tasks', label: 'Tarefas', icon: Check },
    ...(admin ? [{ id: 'admin', label: 'Admin', icon: ShieldCheck } as const] : []),
  ] as const;

  return (
    <nav
      aria-label="Módulos"
      className="fixed inset-x-0 bottom-0 z-40 flex h-16 border-t border-subtle bg-app"
    >
      {destinos.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => setModulo(id)}
          aria-current={modulo === id ? 'page' : undefined}
          className={cn(
            'flex flex-1 cursor-pointer flex-col items-center justify-center gap-1',
            modulo === id ? 'text-accent' : 'text-tertiary',
          )}
        >
          <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
          <span className="text-[11px] font-semibold">{label}</span>
        </button>
      ))}
    </nav>
  );
}

/** Abaixo de 768px: 4 destinos fixos no rodape (Secao 6.9). */
export function BarraInferior({ onCompose }: { onCompose: () => void }) {
  const setPaletteOpen = useUi((s) => s.setPaletteOpen);
  const setFilter = useUi((s) => s.setFilter);
  const filter = useUi((s) => s.filter);

  const destinos = [
    { id: 'all', label: 'Tudo', icon: Inbox, onClick: () => setFilter('all') },
    { id: 'unread', label: 'Não lidos', icon: Mail, onClick: () => setFilter('unread') },
    { id: 'flagged', label: 'Sinalizados', icon: Flag, onClick: () => setFilter('flagged') },
    { id: 'busca', label: 'Buscar', icon: Search, onClick: () => setPaletteOpen(true) },
  ] as const;

  return (
    <>
      <button
        type="button"
        onClick={onCompose}
        aria-label="Escrever"
        className="fixed right-4 bottom-20 z-40 flex size-14 cursor-pointer items-center justify-center rounded-full bg-rail text-white shadow-wc-lg"
      >
        <PenLine size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>

      <nav
        aria-label="Destinos"
        className="fixed inset-x-0 bottom-0 z-40 flex h-16 border-t border-subtle bg-app"
      >
        {destinos.map(({ id, label, icon: Icon, onClick }) => (
          <button
            key={id}
            type="button"
            onClick={onClick}
            aria-current={filter === id ? 'page' : undefined}
            className={cn(
              'flex flex-1 cursor-pointer flex-col items-center justify-center gap-1',
              filter === id ? 'text-accent' : 'text-tertiary',
            )}
          >
            <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
            <span className="text-[11px] font-semibold">{label}</span>
          </button>
        ))}
      </nav>
    </>
  );
}
